import {
  Address,
  AddressType,
  DependentFormData,
  Email,
  EmailType,
  FlagMarker,
  MemberCreateRequest,
  MemberDetails,
  MemberDetailsFormData,
  MemberFormData,
  MemberRelatedInstance,
  MemberUpdateRequest,
  Phone,
  RelatedInstance,
  RelationshipStatus,
  RelationshipSubType,
  RelationshipType,
  WithPreferred,
  BaseUIAddress,
  MemberSourceRegistrationCd,
  MemberToggleStatusData
} from '../../@types/Members';
import { processPreferredFlag, separateAreaCode } from '../../flows/Members/utils';
import moment from 'moment';

const dateIsoFormat = (date: any) => {
  return moment.utc(date).toISOString();
};

const removeRedundantSpaces = (str: string) => str.trim().replace(/ +(?= )/g, '');

export const transformMemberDetailsToClientCommercialCreate: (memberDetails: MemberDetails) => MemberCreateRequest = (
  memberDetails: MemberDetails
): MemberCreateRequest => {
  const { memberMasterMrn, createdAt, updatedAt, exclusionCd, group, readOnly, ...memberInfo } = memberDetails;

  return {
    ...memberInfo,
    mrnSourceClassificationCd: MemberSourceRegistrationCd.CLIENT_COMMERCIAL,
    relatedInstances: memberInfo.primaryFlg === 'Y' ? [] : memberInfo.relatedInstances
  };
};

export const transformOutcomingMemberCreateRequest: (memberFormData: MemberFormData) => MemberCreateRequest = (
  memberFormData: MemberFormData
): MemberCreateRequest => {
  const {
    group: { id, consultReimbursementMethodCode },
    firstNm,
    middleNm,
    lastNm,
    dob,
    genderCd,
    clientId: sourceMrnId,
    effectiveStartDt,
    languageCd
  } = memberFormData;
  const addresses: Address[] = buildAddresses(memberFormData);
  const phones: Phone[] = buildPhones(memberFormData);
  const emails: Email[] = buildEmails(memberFormData);

  return {
    groupId: id,
    consultReimbursementMethodCode,
    firstNm,
    lastNm: removeRedundantSpaces(lastNm),
    middleNm,
    dob: dateIsoFormat(dob),
    genderCd,
    sourceMrnId,
    effectiveStartDt: dateIsoFormat(effectiveStartDt),
    primaryFlg: FlagMarker.Y,
    addresses,
    phones,
    emails: emails && emails.length ? emails : undefined,
    languagePreferences: languageCd ? [{ languageCd }] : undefined
  };
};

export const buildAddresses: (data: MemberFormData) => Address[] = ({
  mailingAddress,
  homeAddress,
  isMailingAddressDifferent
}: MemberFormData): Address[] => {
  const isFullAddress = (addr: BaseUIAddress): boolean =>
    !!(addr && addr?.addressLine1 && addr?.city && addr?.stateProvince && addr?.postal);

  const getFormattedAddress = (addr: BaseUIAddress, addressTypeCd: AddressType): Address[] => [
    {
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2,
      city: addr.city,
      stateProvince: addr.stateProvince,
      postal: addr.postal,
      addressTypeCd,
      preferredFlg: addr?.isPreferred ? FlagMarker.Y : FlagMarker.N
    }
  ];

  const homeAdress = isFullAddress(homeAddress) ? getFormattedAddress(homeAddress, AddressType.ADDRESSTYPE_HOME) : [];
  const mailingAdress = isFullAddress(mailingAddress)
    ? getFormattedAddress(mailingAddress, AddressType.ADDRESSTYPE_MAIL)
    : [];

  return [...homeAdress, ...mailingAdress];
};

const buildPhones: (data: MemberFormData) => Phone[] = ({ ...props }: MemberFormData): Phone[] => {
  const { phone1, phone2, phoneIsPreferred } = props;
  props[phoneIsPreferred].isPreferred = true;
  return [phone1, phone2]
    .filter((phone) => !!phone && phone.phoneNumber && phone.phoneFaxTypeCd)
    .map((phone) => ({
      areaCode: phone.phoneNumber.slice(0, 3),
      phoneFaxTypeCd: phone.phoneFaxTypeCd,
      preferredFlg: phone.isPreferred ? FlagMarker.Y : FlagMarker.N,
      phoneNumber: phone.phoneNumber.slice(3),
      extension: phone.extension
    }));
};

type WithContactEmail = MemberFormData | DependentFormData;
const buildWorkEmail: (data: WithContactEmail) => Email = ({ contactEmail }: WithContactEmail): Email => {
  return {
    emailAddress: contactEmail!,
    emailTypeCd: EmailType.EMAILTYPE_WORK,
    preferredFlg: FlagMarker.Y
  };
};

const buildEmails: (data: MemberFormData) => Email[] = (member: MemberFormData): Email[] => {
  const { contactEmail } = member;
  const result: Email[] = [];

  if (contactEmail) {
    result.push(buildWorkEmail(member));
  }

  return result;
};

export const transformOutcomingDependentCreateRequest = (
  member: DependentFormData,
  primary: MemberDetails
): MemberCreateRequest => {
  const {
    memberMasterMrnId,
    firstNm,
    lastNm,
    middleNm,
    clientId: sourceMrnId,
    dob,
    genderCd,
    languageCd,
    effectiveStartDt
  } = member;
  const emails: Email[] | undefined = member.contactEmail ? [buildWorkEmail(member)] : undefined;
  const relatedInstanceToLink: RelatedInstance = dependentDataToRelatedInstance(primary, member.relationshipSubtypeCd);
  const {
    addresses,
    groupId,
    group: { consultReimbursementMethodCode }
  } = primary;

  return {
    memberMasterMrnId,
    firstNm,
    lastNm: removeRedundantSpaces(lastNm),
    middleNm,
    groupId,
    consultReimbursementMethodCode,
    dob: dateIsoFormat(dob),
    genderCd: genderCd || 'GENDER_UNKNOWN',
    effectiveStartDt: dateIsoFormat(effectiveStartDt),
    primaryFlg: FlagMarker.N,
    sourceMrnId,
    emails,
    addresses,
    languagePreferences: languageCd ? [{ languageCd }] : undefined,
    relatedInstances: [relatedInstanceToLink]
  };
};

const memberRelatedInstacneToRelatedInstanceMeta: (
  relatedInstance: MemberRelatedInstance,
  relationshipSubtypeCdInput: RelationshipSubType | undefined
) => RelatedInstance = (
  relatedInstance: MemberRelatedInstance,
  relationshipSubtypeCdInput: RelationshipSubType | undefined
): RelatedInstance => {
  const { mrnId, relationshipSubtypeCd, relationshipStatusCd, relationshipTypeCd } = relatedInstance;
  return {
    mrnId,
    relationshipSubtypeCd: relationshipSubtypeCdInput || relationshipSubtypeCd,
    relationshipTypeCd: relationshipTypeCd || RelationshipType.RELATIONSHIPTYPE_DEPENDENT,
    relationshipStatusCd: relationshipStatusCd || RelationshipStatus.RELATIONSHIPSTATUS_PENDING
  };
};

const dependentDataToRelatedInstance: (
  { memberMasterMrn }: MemberDetails,
  relationshipSubtypeCd: RelationshipSubType
) => RelatedInstance = (
  { memberMasterMrn }: MemberDetails,
  relationshipSubtypeCd: RelationshipSubType
): RelatedInstance => {
  return {
    mrnId: memberMasterMrn,
    relationshipSubtypeCd,
    relationshipTypeCd: RelationshipType.RELATIONSHIPTYPE_DEPENDENT,
    relationshipStatusCd: RelationshipStatus.RELATIONSHIPSTATUS_PENDING
  };
};

const processFormDataAddress: (address: WithPreferred<Address>, type: AddressType) => Address = (
  address: WithPreferred<Address>,
  type: AddressType
): Address => {
  return {
    ...processPreferredFlag(address),
    addressTypeCd: type
  };
};

const processFormDataPhone = (phone: Phone, phoneIsPreferred: boolean): Phone => {
  phone.preferredFlg = phoneIsPreferred ? FlagMarker.Y : FlagMarker.N;
  return separateAreaCode(phone);
};

const processFormDataEmail: (email: string, type: EmailType) => Email = (email: string, type: EmailType): Email => {
  return {
    emailAddress: email,
    emailTypeCd: type,
    preferredFlg: FlagMarker.Y
  };
};

export const transformOutcomingMemberUpdateRequest: (member: MemberDetailsFormData) => MemberUpdateRequest = (
  member: MemberDetailsFormData
): MemberUpdateRequest => {
  const {
    homeAddress,
    mailingAddress,
    phone1,
    phone2,
    phoneIsPreferred,
    firstNm,
    lastNm,
    middleNm,
    contactEmail,
    dob,
    effectiveStartDt,
    effectiveEndDt,
    genderCd,
    clientId,
    originMember,
    languageCd,
    relatedInstances: relatedInstancesMeta,
    groupId,
    relationshipSubtypeCd
  } = member;
  const {
    relatedInstances,
    createdAt,
    updatedAt,
    exclusionCd,
    addresses,
    phones,
    emails,
    languagePreferences,
    group: { consultReimbursementMethodCode },
    readOnly,
    individualId,
    ...memberDetails
  } = originMember;

  const memberAddresses: Address[] = [];
  const memberPhones: Phone[] = [];
  const memberEmails: Email[] = [];

  if (homeAddress && homeAddress.addressLine1) {
    memberAddresses.push(processFormDataAddress(homeAddress, AddressType.ADDRESSTYPE_HOME));
  }

  if (mailingAddress && mailingAddress.addressLine1) {
    memberAddresses.push(processFormDataAddress(mailingAddress, AddressType.ADDRESSTYPE_MAIL));
  }

  if (phone1 && phone1.phoneFaxTypeCd && phone1.phoneNumber) {
    memberPhones.push(processFormDataPhone(phone1, phoneIsPreferred === 'phone1'));
  }

  if (phone2 && phone2.phoneFaxTypeCd && phone2.phoneNumber) {
    memberPhones.push(processFormDataPhone(phone2, phoneIsPreferred === 'phone2'));
  }

  if (contactEmail) {
    memberEmails.push(processFormDataEmail(contactEmail, EmailType.EMAILTYPE_WORK));
  }

  // append related instances to the dependent member only
  const relatedInstancesData =
    memberDetails.primaryFlg === FlagMarker.N
      ? relatedInstancesMeta?.map((relatedInstance) =>
          memberRelatedInstacneToRelatedInstanceMeta(relatedInstance, relationshipSubtypeCd)
        )
      : undefined;
  return {
    ...memberDetails,
    groupId,
    consultReimbursementMethodCode,
    effectiveStartDt: dateIsoFormat(effectiveStartDt),
    effectiveEndDt: effectiveEndDt ? dateIsoFormat(effectiveEndDt) : effectiveEndDt,
    firstNm,
    lastNm: removeRedundantSpaces(lastNm),
    middleNm,
    dob: dateIsoFormat(dob),
    genderCd,
    sourceMrnId: clientId,
    addresses: memberAddresses,
    emails: memberEmails,
    phones: memberPhones,
    languagePreferences: languageCd ? [{ languageCd }] : undefined,
    relatedInstances: relatedInstancesData || undefined
  };
};

export const transformOutgoingMemberStatusToggleRequest = (data: MemberToggleStatusData) => {
  const { memberId, action, effectiveEndDt } = data;
  return {
    memberId,
    action,
    effectiveEndDt: effectiveEndDt ? dateIsoFormat(effectiveEndDt) : undefined
  };
};
